import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html, RoundedBox, Text, Float } from '@react-three/drei';
import * as THREE from 'three';
import simulationData from '../../data/simulation_results.json';

// Particle representing an ANF activation
const Particle = ({ position, color, status, target, onComplete }) => {
    const mesh = useRef();
    const [active, setActive] = useState(true);

    useFrame((state, delta) => {
        if (!mesh.current || !active) return;

        // Move towards target
        const step = delta * 4; // Speed
        mesh.current.position.x = THREE.MathUtils.lerp(mesh.current.position.x, target[0], step * 0.5);

        // If close to target, "complete"
        if (Math.abs(mesh.current.position.x - target[0]) < 0.1) {
            if (status === 'conflict') {
                mesh.current.material.color.set('#ef4444'); // Turn red
            } else {
                mesh.current.material.color.set('#22c55e'); // Turn green
            }

            // Fade out or shrink
            mesh.current.scale.multiplyScalar(0.9);
            if (mesh.current.scale.x < 0.01) {
                setActive(false);
                if (onComplete) onComplete();
            }
        }
    });

    if (!active) return null;

    return (
        <mesh ref={mesh} position={position}>
            <sphereGeometry args={[0.15, 16, 16]} />
            <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.5} />
        </mesh>
    );
};

const Lane = ({ title, data, position }) => {
    const [particles, setParticles] = useState([]);
    const [stats, setStats] = useState({ total: 0, conflicts: 0, scenario: 1 });

    const scenarioIndex = useRef(0);
    const activationIndex = useRef(0);
    const scenarioTime = useRef(0);

    useFrame((state, delta) => {
        // Advance time
        scenarioTime.current += delta * 2; // 2x speed multiplier for viewing comfort

        const currentScenario = data[scenarioIndex.current];

        // Check for new activations to spawn
        while (activationIndex.current < currentScenario.activations.length) {
            const activation = currentScenario.activations[activationIndex.current];

            if (scenarioTime.current >= activation.start_time) {
                // Spawn!
                const isConflict = activation.is_conflict;
                const id = Math.random();

                setParticles(prev => [
                    ...prev.slice(-30), // Keep last 30
                    {
                        id,
                        x: -4,
                        isConflict,
                        color: isConflict ? '#f59e0b' : '#3b82f6'
                    }
                ]);

                setStats(prev => ({
                    total: prev.total + 1,
                    conflicts: prev.conflicts + (isConflict ? 1 : 0),
                    scenario: currentScenario.scenario_id
                }));

                activationIndex.current++;
            } else {
                break; // Next activation is in the future
            }
        }

        // Check for scenario end
        if (scenarioTime.current > currentScenario.total_duration) {
            // Reset for next scenario
            scenarioIndex.current = (scenarioIndex.current + 1) % data.length;
            activationIndex.current = 0;
            scenarioTime.current = 0;
            // Optional: Clear particles or let them flow out? Let them flow.
        }
    });

    return (
        <group position={position}>
            {/* Lane Label - Dark Color */}
            <Text position={[-4, 1, 0]} fontSize={0.3} color="#334155" anchorX="left">
                {title}
            </Text>

            {/* Stats Board - Moved further right and back to avoid overlap */}
            <group position={[5.0, 0.5, -0.5]}>
                <RoundedBox args={[2.8, 1.8, 0.1]} radius={0.1}>
                    <meshStandardMaterial color="#1e293b" />
                </RoundedBox>
                <Text position={[-1.2, 0.5, 0.1]} fontSize={0.12} color="#94a3b8" anchorX="left">
                    Scenario: {stats.scenario}
                </Text>
                <Text position={[-1.2, 0.2, 0.1]} fontSize={0.15} color="#cbd5e1" anchorX="left">
                    Activations: {stats.total}
                </Text>
                <Text position={[-1.2, -0.1, 0.1]} fontSize={0.15} color={stats.conflicts / stats.total > 0.1 ? "#ef4444" : "#22c55e"} anchorX="left">
                    Conflicts: {stats.conflicts}
                </Text>
                <Text position={[-1.2, -0.4, 0.1]} fontSize={0.15} color="white" anchorX="left">
                    Rate: {stats.total > 0 ? ((stats.conflicts / stats.total) * 100).toFixed(1) : 0}%
                </Text>
            </group>

            {/* Track */}
            <mesh position={[0, -0.2, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                <planeGeometry args={[8, 0.5]} />
                <meshStandardMaterial color="#334155" opacity={0.5} transparent />
            </mesh>

            {/* Particles - Target x=3.5 to stop before board */}
            {particles.map(p => (
                <Particle
                    key={p.id}
                    position={[p.x, 0, 0]}
                    color={p.color}
                    status={p.isConflict ? 'conflict' : 'safe'}
                    target={[3.5, 0, 0]}
                />
            ))}
        </group>
    );
};

export const SimulationModel = ({ onShowDetails }) => {
    return (
        <group>
            <Float speed={1} rotationIntensity={0.1} floatIntensity={0.2}>
                {/* Title - Dark Color for Light Background */}
                <Text position={[0, 3, 0]} fontSize={0.5} color="#1e293b" anchorX="center">
                    ASANA Sequencing Simulation (N=15 Scenarios)
                </Text>

                {/* Baseline Lane (High Conflict) */}
                <Lane
                    title="Baseline (No NCF)"
                    data={simulationData.baseline}
                    position={[0, 1, 0]}
                />

                {/* NCF Lane (Low Conflict) */}
                <Lane
                    title="With NCF (Normative Control)"
                    data={simulationData.ncf}
                    position={[0, -1.5, 0]}
                />
            </Float>

            {/* View Logs Button - Moved outside Float for stability, High Contrast Blue */}
            <Html position={[0, 2.2, 0]} center zIndexRange={[100, 0]}>
                <button
                    onClick={onShowDetails}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg transition-all flex items-center gap-2 group transform hover:scale-105"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                    View Sequencing Logs
                </button>
            </Html>
        </group>
    );
};
